/**
 * MobileScan Page (P4 Mobile-Scan, Feb 2026)
 *
 * Mobile-first Quick-Scan-Page für Belege & Lieferantenrechnungen.
 *
 * Flow:
 *   Mobile (Phone):  Camera → Capture → [Multi-Page-Loop] → PDF-Stitching →
 *                    POST /api/expenses/analyze-receipt-ai → AI-Result-Card →
 *                    Save (POST /api/expenses) oder Korrigieren
 *
 *   Desktop:         Zeigt QR-Code zur eigenen URL → User öffnet auf Smartphone
 *
 * Auth: Standard JWT, „Angemeldet bleiben" (90d) im Login empfohlen.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Camera, Plus, Check, X, Loader2, FileText, Smartphone, Trash2, Receipt, AlertTriangle, Clock, CalendarDays, Crop,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useIsPro } from '@/hooks/useIsPro';
import useIsMobile from '@/utils/useIsMobile';
import MobileScannerCamera from '@/components/MobileScannerCamera';
import ReceiptCropEditor from '@/components/ReceiptCropEditor';
import UpgradeRequiredView from '@/components/UpgradeRequiredView';
import { Smartphone as SmartphoneIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

// Juni 2026 — Multi-Steuersatz aus KI-Extraktion (tax_breakdown) in den Expense-Payload
// übernehmen. Bei >=2 echten Sätzen: tax_breakdown + Netto/USt-Summen + dominanter Satz,
// sonst Einzel-Satz wie bisher. Behebt: gescannte Belege mit zwei MwSt-Sätzen verloren den
// zweiten Satz beim Speichern.
const buildTaxFields = (ed) => {
  const bd = Array.isArray(ed.tax_breakdown)
    ? ed.tax_breakdown
        .filter((tb) => tb && (Number(tb.net) > 0 || Number(tb.vat) > 0))
        .map((tb) => ({
          rate: Number(tb.rate) || 0,
          net: Math.round((Number(tb.net) || 0) * 100) / 100,
          vat: Math.round((Number(tb.vat) || 0) * 100) / 100,
        }))
    : [];
  if (bd.length >= 2) {
    const sumNet = Math.round(bd.reduce((s, tb) => s + tb.net, 0) * 100) / 100;
    const sumVat = Math.round(bd.reduce((s, tb) => s + tb.vat, 0) * 100) / 100;
    const dom = [...bd].sort((a, b) => (b.net + b.vat) - (a.net + a.vat))[0];
    return {
      tax_breakdown: bd,
      multi_tax_suspected: true,
      net_amount: sumNet,
      vat_amount: sumVat,
      vat_rate: dom ? dom.rate : (ed.vat_rate ?? 19.0),
    };
  }
  return { vat_rate: ed.vat_rate ?? 19.0, net_amount: ed.net_amount ?? null };
};

// Jun 2026 — Schritt-Labels + sprechende Fehlermeldungen für die Stapel-Pipeline
const STEP_LABELS = { upload: 'Lädt hoch…', ai: 'KI liest aus…', save: 'Speichert…' };
const STEP_NAMES = { upload: 'Upload', ai: 'KI-Analyse', save: 'Speichern' };

const describeStepError = (step, e) => {
  const d = e?.response?.data?.detail;
  let reason;
  if (!e?.response) reason = 'Netzwerkfehler – Verbindung prüfen';
  else if (typeof d === 'string') reason = d;
  else if (Array.isArray(d)) reason = d.map(x => x?.msg).filter(Boolean).join(', ') || 'Ungültige Daten';
  else reason = `HTTP ${e.response.status}`;
  return `${STEP_NAMES[step] || 'Analyse'} fehlgeschlagen: ${reason}`;
};

// Sep 2026 — Kategorie direkt in der Stapel-Liste umstellen (gleiche Liste wie im Drawer)
const EXPENSE_CATEGORIES = [
  { key: 'office', label: 'Büromaterial' }, { key: 'software', label: 'Software & Lizenzen' },
  { key: 'travel', label: 'Reisekosten' }, { key: 'hospitality', label: 'Bewirtung' },
  { key: 'vehicle', label: 'Fahrzeug & Fahrkosten' }, { key: 'telecom', label: 'Telefon & Internet' },
  { key: 'insurance', label: 'Versicherungen' }, { key: 'education', label: 'Fortbildung' },
  { key: 'marketing', label: 'Werbung & Marketing' }, { key: 'rent', label: 'Miete & Nebenkosten' },
  { key: 'consulting', label: 'Beratung (StB/Anwalt)' }, { key: 'equipment', label: 'Betriebsausstattung' },
  { key: 'other', label: 'Sonstiges' },
];
const catLabel = (key) => EXPENSE_CATEGORIES.find(c => c.key === key)?.label || key || 'Sonstiges';

export default function MobileScan() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isPro = useIsPro();
  const isMobile = useIsMobile();

  // Multi-Page-State: Liste captured Pages (Blobs + Object-URL für Thumbnails).
  const [pages, setPages] = useState([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [todayCount, setTodayCount] = useState(0);
  // Feb 2026 — Stapel-Modus (Schnellerfassung): Mehrere Belege in Folge ohne Detail-Sheet
  const [quickMode, setQuickMode] = useState(() => {
    try { return localStorage.getItem('scan_quick_mode') === '1'; } catch { return false; }
  });
  const [stackList, setStackList] = useState([]); // Session-Belege: [{id, vendor, amount, currency, expenseId, category}]
  // Jun 2026 — Fortschritt der sequenziellen Stapel-Verarbeitung ({total, done, failed} | null)
  const [batchProgress, setBatchProgress] = useState(null);
  // Jun 2026 — Ref auf pages für die Batch-Pipeline (frische Blobs nach Auto-Crop)
  const pagesRef = useRef([]);

  // Daily-Counter laden (motivierend)
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/expenses?limit=200`, { headers: H() })
      .then(res => {
        const today = new Date().toISOString().slice(0, 10);
        const count = (res.data || []).filter(e => (e.created_at || '').slice(0, 10) === today).length;
        setTodayCount(count);
      })
      .catch(() => {});
  }, [user]);

  const [cameraStream, setCameraStream] = useState(null);

  useEffect(() => { pagesRef.current = pages; }, [pages]);

  // iOS-26-tauglicher Camera-Open: getUserMedia direkt im User-Gesture-Handler.
  // iOS Safari hängt ab iOS 16+ play() an die ursprüngliche Click-Gesture; deshalb
  // dürfen wir getUserMedia NICHT erst nach einem React-Render-Cycle (in useEffect
  // einer gemounteten Komponente) auslösen — sonst silent stuck.
  const openCamera = useCallback(async () => {
    try {
      // Sep 2026 — Maximale Sensor-Auflösung anfordern (Default wäre nur 640×480).
      const hiRes = { width: { ideal: 4096 }, height: { ideal: 3072 } };
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, ...hiRes },
          audio: false,
        });
      } catch (e1) {
        // eslint-disable-next-line no-console
        console.warn('[MobileScan] env-cam failed, retry generic:', e1);
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: hiRes, audio: false });
        } catch (e2) {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        }
      }
      setCameraStream(stream);
      setCameraOpen(true);
    } catch (e) {
      const name = e?.name || '';
      if (name === 'NotAllowedError') toast.error('Kamera-Zugriff verweigert. Bitte in den Einstellungen erlauben.');
      else if (name === 'NotFoundError') toast.error('Keine Kamera gefunden.');
      else if (name === 'NotReadableError') toast.error('Kamera wird gerade verwendet. Bitte andere Apps schließen.');
      else toast.error(`Kamera-Fehler: ${e?.message || name || 'Unbekannt'}`);
    }
  }, []);

  const closeCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
    }
    setCameraStream(null);
    setCameraOpen(false);
  }, [cameraStream]);

  // ── Page-Stack-Manipulation ──
  // Beim Capture: Server-side Auto-Crop läuft im Hintergrund (~300-500ms). Bei
  // Detection-Fehler kommt der Original-Blob zurück — der User merkt nichts.
  // Jun 2026 — Stapel-Modus 2.0: Capture landet nur noch in der Warteschlange,
  // die Kamera bleibt offen. Analyse startet erst per Klick auf „Analysieren".
  // Sep 2026 — Schneller Ablauf (1 Upload statt 4): Foto sofort als Seite anzeigen, im
  // Hintergrund EINMAL hochladen (scan-photo) + serverseitig Auto-Zuschnitt. Editor mit
  // verschiebbaren Ecken nur, wenn keine Ecken erkannt wurden — oder auf Wunsch per Thumbnail.
  const [cropDraft, setCropDraft] = useState(null); // { pageId }
  const [cropBusy, setCropBusy] = useState(false);
  const uploadPromisesRef = useRef({});
  const cropDraftRef = useRef(null);
  useEffect(() => { cropDraftRef.current = cropDraft; }, [cropDraft]);

  const patchPage = useCallback((pageId, patch) => {
    setPages(prev => prev.map(p => (p.id === pageId ? { ...p, ...(typeof patch === 'function' ? patch(p) : patch) } : p)));
  }, []);

  const uploadPhoto = useCallback(async (blob) => {
    const fd = new FormData();
    fd.append('file', blob, 'capture.jpg');
    const res = await axios.post(`${API}/expenses/scan-photo`, fd, {
      headers: { ...H(), 'Content-Type': 'multipart/form-data' }, timeout: 60000,
    });
    return res.data.photo_id;
  }, []);

  const uploadAndAutoCrop = useCallback(async (pageId, blob) => {
    let photoId = null;
    try {
      photoId = await uploadPhoto(blob);
      patchPage(pageId, { photoId });
      const res = await axios.post(`${API}/expenses/scan-photo/${photoId}/auto-crop`, {}, { headers: H(), timeout: 30000 });
      if (res.data?.found && res.data.preview_b64) {
        patchPage(pageId, { url: `data:image/jpeg;base64,${res.data.preview_b64}`, quad: res.data.quad, cropping: false, needsCrop: false });
      } else {
        patchPage(pageId, { cropping: false, needsCrop: true, quad: res.data?.quad || null });
        if (!cropDraftRef.current) setCropDraft({ pageId });
      }
    } catch (e) {
      // Upload/Auto-Crop fehlgeschlagen → Original bleibt, Upload wird bei „Analysieren" nachgeholt
      // eslint-disable-next-line no-console
      console.warn('[MobileScan] scan-photo/auto-crop failed:', e?.message || e);
      patchPage(pageId, { cropping: false });
    }
    return photoId;
  }, [uploadPhoto, patchPage]);

  const handleCapture = useCallback((blob) => {
    const url = URL.createObjectURL(blob);
    const pageId = `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setPages(prev => [...prev, { id: pageId, blob, url, originalUrl: url, cropping: true, status: 'pending', photoId: null, quad: null }]);
    uploadPromisesRef.current[pageId] = uploadAndAutoCrop(pageId, blob);
    // Klassischer Modus: Kamera schließen. Stapel-Modus: offen lassen für das nächste Foto.
    if (!quickMode) closeCamera();
  }, [closeCamera, quickMode, uploadAndAutoCrop]);

  const ensurePhotoId = useCallback(async (page) => {
    if (page.photoId) return page.photoId;
    const pending = uploadPromisesRef.current[page.id];
    if (pending) { const id = await pending; if (id) return id; }
    const id = await uploadPhoto(page.blob);
    patchPage(page.id, { photoId: id });
    return id;
  }, [uploadPhoto, patchPage]);

  const cropPage = pagesRef.current.find(p => p.id === cropDraft?.pageId) || null;

  const finishCrop = useCallback(async (quad) => {
    if (!cropPage) return;
    setCropBusy(true);
    try {
      const photoId = await ensurePhotoId(cropPage);
      const res = await axios.post(`${API}/expenses/scan-photo/${photoId}/crop`, { quad }, {
        headers: H(), responseType: 'blob', timeout: 30000,
      });
      patchPage(cropPage.id, { url: URL.createObjectURL(res.data), quad, needsCrop: false, cropping: false });
      setCropDraft(null);
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? null : e.response?.data?.detail;
      toast.error(e.response?.status === 422 ? 'Zuschnitt nicht möglich — Ecken prüfen.' : (detail || 'Zuschnitt fehlgeschlagen — bitte erneut versuchen.'));
    } finally {
      setCropBusy(false);
    }
  }, [cropPage, ensurePhotoId, patchPage]);

  const skipCrop = useCallback(async () => {
    if (!cropPage) return;
    setCropBusy(true);
    try {
      if (cropPage.photoId) {
        await axios.post(`${API}/expenses/scan-photo/${cropPage.photoId}/crop`, { quad: null }, { headers: H(), responseType: 'blob', timeout: 20000 });
      }
      patchPage(cropPage.id, { url: cropPage.originalUrl, quad: null, needsCrop: false, cropping: false });
      setCropDraft(null);
    } catch {
      toast.error('Konnte Zuschnitt nicht zurücksetzen.');
    } finally {
      setCropBusy(false);
    }
  }, [cropPage, patchPage]);

  const retakeCrop = useCallback(() => {
    if (!cropPage) return;
    if (cropPage.photoId) axios.delete(`${API}/expenses/scan-photo/${cropPage.photoId}`, { headers: H() }).catch(() => {});
    setPages(prev => prev.filter(x => x.id !== cropPage.id));
    setCropDraft(null);
  }, [cropPage]);

  const redetectCrop = useCallback(async () => {
    if (!cropPage) return;
    setCropBusy(true);
    try {
      const photoId = await ensurePhotoId(cropPage);
      const res = await axios.post(`${API}/expenses/scan-photo/${photoId}/auto-crop`, {}, { headers: H(), timeout: 30000 });
      if (res.data?.found) {
        patchPage(cropPage.id, { quad: res.data.quad, url: `data:image/jpeg;base64,${res.data.preview_b64}`, needsCrop: false });
        toast.success('Ecken erkannt', { duration: 1500 });
      } else {
        toast.error('Kein Beleg erkannt — Ecken bitte manuell setzen.');
      }
    } catch {
      toast.error('Erkennung fehlgeschlagen.');
    } finally {
      setCropBusy(false);
    }
  }, [cropPage, ensurePhotoId, patchPage]);

  // Nach dem Editor: nächste Seite mit offenem Zuschnitt automatisch anbieten
  useEffect(() => {
    if (cropDraft) return;
    const next = pages.find(p => p.needsCrop && !p.cropping);
    if (next) setCropDraft({ pageId: next.id });
  }, [cropDraft, pages]);

  const removePage = (id) => {
    setPages(prev => {
      const p = prev.find(x => x.id === id);
      if (p?.url) URL.revokeObjectURL(p.url);
      return prev.filter(x => x.id !== id);
    });
  };

  const resetAll = () => {
    pages.forEach(p => URL.revokeObjectURL(p.url));
    setPages([]);
    setAiResult(null);
  };

  // Jun 2026 — Stapel-Modus 2.0 Batch-Pipeline: Alle Fotos der Warteschlange werden
  // mit EINEM Klick verarbeitet — strikt NACHEINANDER (1 Beleg zur Zeit), pro Beleg
  // in drei sichtbaren Schritten: Upload → KI-Auslese → Speichern. Transiente Fehler
  // (Netzwerk/5xx) → 1× Auto-Retry nur des betroffenen Schritts (keine doppelten
  // Uploads/KI-Kosten). Fehlgeschlagene Belege bleiben mit Fehler-Badge in der Liste
  // und werden beim nächsten „Analysieren" erneut versucht.
  const setPageStep = (pageId, step) =>
    setPages(prev => prev.map(p => p.id === pageId ? { ...p, step } : p));

  const withRetry = async (fn) => {
    try {
      return await fn();
    } catch (e) {
      const status = e.response?.status;
      const transient = !status || status >= 500;
      if (!transient) throw e;
      return fn();
    }
  };

  const analyzeAndSaveSinglePage = async (pageId) => {
    const page = pagesRef.current.find(p => p.id === pageId);
    if (!page) return true;
    let step = 'upload';
    try {
      setPageStep(pageId, 'upload');
      const photoId = await withRetry(() => ensurePhotoId(page));

      step = 'ai';
      setPageStep(pageId, 'ai');
      const finRes = await withRetry(() => axios.post(`${API}/expenses/scan-photo/${photoId}/finalize`, {}, { headers: H(), timeout: 120000 }));
      const upRes = { data: finRes.data };
      const ed = finRes.data?.analysis?.extracted_data || {};

      step = 'save';
      setPageStep(pageId, 'save');

      const payload = {
        description: ed.description || ed.vendor || 'Beleg-Scan',
        amount: Number(ed.amount || 0),
        date: ed.date ? new Date(ed.date).toISOString() : new Date().toISOString(),
        category: ed.category_suggestion || 'other',
        hospitality_location: ed.category_suggestion === 'hospitality' ? (ed.hospitality_location || null) : null,
        ...buildTaxFields(ed),
        vendor: ed.vendor || null,
        invoice_number: ed.invoice_number || null,
        receipt_file_key: upRes.data.file_key,
        receipt_filename: upRes.data.filename,
        receipt_content_type: upRes.data.content_type,
        storage_uri: upRes.data.storage_uri,
        extraction_method: 'ai',
        source: 'mobile_scan',
      };
      const expRes = await withRetry(() => axios.post(`${API}/expenses`, payload, { headers: H() }));
      const stackEntry = {
        id: pageId,
        expenseId: expRes.data?.id || null,
        vendor: ed.vendor || 'Unbekannt',
        amount: Number(ed.amount || 0),
        currency: ed.currency || 'EUR',
        category: ed.category_suggestion || 'other',
        date: String(ed.date || new Date().toISOString()).slice(0, 10),
      };
      setStackList(prev => [stackEntry, ...prev]);
      setTodayCount(c => c + 1);
      setPages(prev => {
        const done = prev.find(p => p.id === pageId);
        if (done?.url) URL.revokeObjectURL(done.url);
        return prev.filter(p => p.id !== pageId);
      });
      return true;
    } catch (e) {
      setPages(prev => prev.map(p => p.id === pageId
        ? { ...p, status: 'error', step: null, errorMsg: describeStepError(step, e) }
        : p));
      return false;
    }
  };

  // Sep 2026 — Kategorie direkt aus der Stapel-Liste umstellen (Bottom-Sheet)
  const [categoryPickerFor, setCategoryPickerFor] = useState(null);
  const updateStackCategory = async (entry, category) => {
    setCategoryPickerFor(null);
    if (!entry.expenseId || category === entry.category) return;
    const country = (user?.country || 'DE').toUpperCase();
    const patch = { category };
    // Bewirtung (§4 EStG): länderspezifische Absetzquote wie im Drawer/Backend-Create
    if (category === 'hospitality') {
      const pct = country === 'AT' ? 50 : (country === 'CH' ? 100 : 70);
      patch.deductible_percentage = pct;
      patch.tax_deductible_type = pct < 100 ? 'partial' : 'full';
    } else if (entry.category === 'hospitality') {
      patch.deductible_percentage = 100;
      patch.tax_deductible_type = 'full';
    }
    setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, categorySaving: true } : x));
    try {
      await axios.put(`${API}/expenses/${entry.expenseId}`, patch, { headers: H() });
      setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, category, categorySaving: false } : x));
      toast.success(`Kategorie: ${catLabel(category)}`, { duration: 1800 });
    } catch (e) {
      setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, categorySaving: false } : x));
      toast.error(e.response?.data?.detail || 'Kategorie konnte nicht gespeichert werden');
    }
  };

  // Sep 2026 — Betrag direkt aus der Stapel-Liste korrigieren (ohne Drawer)
  const [editingAmountId, setEditingAmountId] = useState(null);
  const [amountDraft, setAmountDraft] = useState('');
  const editingAmountRef = useRef(null); // synchroner Spiegel gegen Doppel-Commit (Submit + Blur)
  const commitStackAmount = async (entry) => {
    if (editingAmountRef.current !== entry.id) return;
    editingAmountRef.current = null;
    setEditingAmountId(null);
    const parsed = parseFloat(String(amountDraft).replace(/\s|€/g, '').replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed < 0 || Math.abs(parsed - entry.amount) < 0.005) return;
    const amount = Math.round(parsed * 100) / 100;
    setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, amountSaving: true } : x));
    try {
      await axios.put(`${API}/expenses/${entry.expenseId}`, { amount }, { headers: H() });
      setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, amount, amountSaving: false } : x));
      toast.success(`Betrag auf ${amount.toFixed(2)} ${entry.currency} geändert`, { duration: 1800 });
    } catch (e) {
      setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, amountSaving: false } : x));
      toast.error(e.response?.data?.detail || 'Betrag konnte nicht gespeichert werden');
    }
  };

  // Sep 2026 — Belegdatum direkt aus der Stapel-Liste korrigieren (ohne Drawer)
  const updateStackDate = async (entry, newDate) => {
    if (!newDate || !entry.expenseId || newDate === entry.date) return;
    setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, dateSaving: true } : x));
    try {
      await axios.put(`${API}/expenses/${entry.expenseId}`, { date: new Date(newDate).toISOString() }, { headers: H() });
      setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, date: newDate, dateSaving: false } : x));
      toast.success(`Belegdatum auf ${newDate} geändert`, { duration: 1800 });
    } catch (e) {
      setStackList(prev => prev.map(x => x.id === entry.id ? { ...x, dateSaving: false } : x));
      toast.error(e.response?.data?.detail || 'Datum konnte nicht gespeichert werden');
    }
  };

  const processQueue = async () => {
    const ids = pagesRef.current.map(p => p.id);
    if (!ids.length || analyzing) return;
    setAnalyzing(true);
    setPages(prev => prev.map(p => ({ ...p, status: 'queued', step: null, errorMsg: null })));
    setBatchProgress({ total: ids.length, done: 0, failed: 0 });
    let failed = 0;
    for (let i = 0; i < ids.length; i += 1) {
      const id = ids[i];
      setPages(prev => prev.map(p => p.id === id ? { ...p, status: 'processing' } : p));
      const ok = await analyzeAndSaveSinglePage(id);
      if (!ok) failed += 1;
      setBatchProgress({ total: ids.length, done: i + 1, failed });
    }
    setAnalyzing(false);
    setBatchProgress(null);
    if (failed > 0) {
      toast.error(`${failed} von ${ids.length} Beleg${ids.length === 1 ? '' : 'en'} fehlgeschlagen — Details am Beleg, „Analysieren" erneut tippen für einen neuen Versuch.`, { duration: 7000 });
    } else {
      toast.success(`Alle ${ids.length} Belege gespeichert ✓`, { duration: 2200 });
    }
  };


  const analyzePages = async () => {
    if (!pages.length) return;
    setAnalyzing(true);
    try {
      let fileToSend;
      let fileName;
      if (pages.length === 1) {
        // Einzelbeleg: bereits hochgeladenes (zugeschnittenes) Foto → Storage + KI in einem Aufruf
        const photoId = await ensurePhotoId(pages[0]);
        const fin = await axios.post(`${API}/expenses/scan-photo/${photoId}/finalize`, {}, { headers: H(), timeout: 120000 });
        setAiResult({ ...fin.data.analysis, uploadInfo: fin.data, fileName: fin.data.filename });
        return;
      } else {
        // Multi-Page: Zu PDF stitchen (pdf-lib läuft pure-JS im Browser)
        const { PDFDocument } = await import('pdf-lib');
        const pdf = await PDFDocument.create();
        for (const p of pages) {
          const bytes = await p.blob.arrayBuffer();
          const img = await pdf.embedJpg(bytes);
          const page = pdf.addPage([img.width, img.height]);
          page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
        }
        const pdfBytes = await pdf.save();
        fileToSend = new Blob([pdfBytes], { type: 'application/pdf' });
        fileName = `beleg-multipage-${Date.now()}.pdf`;
      }

      const fd = new FormData();
      fd.append('file', fileToSend, fileName);
      const res = await axios.post(`${API}/expenses/analyze-receipt-ai`, fd, {
        headers: { ...H(), 'Content-Type': 'multipart/form-data' },
      });
      const result = { ...res.data, fileName, fileBlob: fileToSend };
      setAiResult(result);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(false);
    }
  };

  // ── Speichern: 2-Step. Upload Receipt → /expenses mit file_key. ──
  const saveExpense = async () => {
    if (!aiResult) return;
    setSaving(true);
    try {
      const ed = aiResult.extracted_data || {};

      // 1) Datei in Storage hochladen (entfällt, wenn finalize bereits gespeichert hat)
      let upRes;
      if (aiResult.uploadInfo) {
        upRes = { data: aiResult.uploadInfo };
      } else {
        const fdUp = new FormData();
        fdUp.append('file', aiResult.fileBlob, aiResult.fileName);
        upRes = await axios.post(`${API}/expenses/upload-receipt`, fdUp, {
          headers: { ...H(), 'Content-Type': 'multipart/form-data' },
        });
      }

      // 2) Expense-Eintrag mit Receipt-Reference
      const payload = {
        description: ed.description || ed.vendor || 'Beleg-Scan',
        amount: Number(ed.amount || 0),
        date: ed.date ? new Date(ed.date).toISOString() : new Date().toISOString(),
        category: ed.category_suggestion || 'other',
        hospitality_location: ed.category_suggestion === 'hospitality' ? (ed.hospitality_location || null) : null,
        ...buildTaxFields(ed),
        vendor: ed.vendor || null,
        invoice_number: ed.invoice_number || null,
        receipt_file_key: upRes.data.file_key,
        receipt_filename: upRes.data.filename,
        receipt_content_type: upRes.data.content_type,
        storage_uri: upRes.data.storage_uri,
        extraction_method: 'ai',
        source: 'mobile_scan',
      };
      await axios.post(`${API}/expenses`, payload, { headers: H() });

      // Session-Liste pflegen
      const stackEntry = {
        id: Date.now(),
        vendor: ed.vendor || 'Unbekannt',
        amount: Number(ed.amount || 0),
        currency: ed.currency || 'EUR',
        date: String(ed.date || new Date().toISOString()).slice(0, 10),
      };
      toast.success(`Beleg gespeichert: ${stackEntry.vendor} · ${stackEntry.amount.toFixed(2)} ${stackEntry.currency}`);
      setStackList(prev => [stackEntry, ...prev]);
      setTodayCount(c => c + 1);
      resetAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  // ── Auth-Wall ──
  if (authLoading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>;
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-white" data-testid="scan-unauth">
        <div className="text-center max-w-sm">
          <Receipt className="h-12 w-12 mx-auto mb-4 text-emerald-400" />
          <h1 className="text-xl font-bold mb-2">Beleg-Scan</h1>
          <p className="text-sm text-slate-300 mb-6">
            Anmeldung erforderlich. Tipp: Aktiviere im Login „Angemeldet bleiben (90 Tage)" für komfortables tägliches Scannen.
          </p>
          <Button onClick={() => navigate('/login?redirect=/m/scan')} className="bg-emerald-500 hover:bg-emerald-600">
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  // ── Pro-Gating (Iter 73i): Free-User → Upgrade-Card ──
  if (!isPro) {
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-4">
        <UpgradeRequiredView
          source="mobile_scan"
          icon={SmartphoneIcon}
          title="Belege per Smartphone scannen"
          subtitle="Schnell, mobil und mit Auto-Crop für Bürokratie zwischendurch."
          features={[
            'Belege direkt mit der Handy-Kamera erfassen',
            'Server-side Auto-Crop für gerade, lesbare Aufnahmen',
            'KI-gestützte automatische Datenerkennung (Datum, Betrag, MwSt.)',
            'Mehrseitige Belege als zusammenhängendes PDF',
            '90-Tage-Login: nie wieder einloggen müssen',
          ]}
        />
      </div>
    );
  }

  // ── Desktop-Fallback: QR-Code zur eigenen URL ──
  if (!isMobile) {
    return <DesktopRedirect />;
  }

  // ── Zuschnitt-Editor (nach jedem Foto) ──
  if (cropDraft && cropPage) {
    return (
      <ReceiptCropEditor
        imageUrl={cropPage.originalUrl}
        initialQuad={cropPage.quad}
        detected={!!cropPage.quad && !cropPage.needsCrop}
        busy={cropBusy}
        onConfirm={finishCrop}
        onSkip={skipCrop}
        onRetake={retakeCrop}
        onRedetect={redetectCrop}
      />
    );
  }

  // ── Camera-Vollbild ──
  if (cameraOpen) {
    return (
      <MobileScannerCamera
        stream={cameraStream}
        onCapture={handleCapture}
        onCancel={closeCamera}
        pageCount={pages.length}
        quickMode={quickMode}
        quickQueuedCount={pages.length}
      />
    );
  }

  // ── AI-Result-Card ──
  if (aiResult) {
    return <AiResultView aiResult={aiResult} saving={saving} onSave={saveExpense} onDiscard={resetAll}
      onCorrect={() => setAiResult(null)} />;
  }

  // ── Hauptansicht: Pages-Übersicht / Capture-CTA ──
  const toggleQuickMode = () => {
    const next = !quickMode;
    try { localStorage.setItem('scan_quick_mode', next ? '1' : '0'); } catch {}
    setQuickMode(next);
    if (next) {
      toast(`Stapel-Modus aktiv: Erst alle Belege fotografieren, dann einmal analysieren`, { duration: 2200 });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="scan-home">
      {/* Header */}
      <header className="px-4 py-3 flex items-center justify-between border-b border-slate-800">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-slate-400" data-testid="scan-back-btn">
          <X className="h-5 w-5" />
        </button>
        <div className="text-sm font-semibold">Beleg-Scan</div>
        <div className="text-xs text-slate-400">Heute: {todayCount}</div>
      </header>

      {/* Feb 2026 — Stapel-Modus-Toggle (sichtbar im Hauptbildschirm) */}
      <div className="px-4 pt-3 pb-1">
        <button
          onClick={toggleQuickMode}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition ${
            quickMode
              ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
              : 'bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800'
          }`}
          data-testid="scan-quick-mode-toggle"
        >
          <div className="flex items-center gap-2.5">
            <div className={`relative w-9 h-5 rounded-full transition ${quickMode ? 'bg-emerald-500' : 'bg-slate-700'}`}>
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${quickMode ? 'left-[18px]' : 'left-0.5'}`} />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold">Stapel-Modus</div>
              <div className="text-[10px] opacity-80">
                {quickMode ? 'Schnell fotografieren, dann einmal analysieren' : 'Klassischer Modus: jeder Beleg einzeln prüfen'}
              </div>
            </div>
          </div>
          {stackList.length > 0 && (
            <div className="text-xs font-bold bg-emerald-500/20 text-emerald-200 px-2 py-1 rounded">
              {stackList.length} ↑
            </div>
          )}
        </button>
      </div>

      {/* Stapel-Liste (Session-Belege) */}
      {stackList.length > 0 && (
        <div className="px-4 pt-2" data-testid="scan-stack-list">
          {/* Sep 2026 — Datums-Warnung: erkanntes Belegdatum außerhalb des laufenden Jahres */}
          {(() => {
            const year = String(new Date().getFullYear());
            const offYear = stackList.filter(s => s.date && !String(s.date).startsWith(year)).length;
            if (!offYear) return null;
            return (
              <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-200"
                data-testid="scan-stack-offyear-hint">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-400" />
                <span>
                  <strong>{offYear} {offYear === 1 ? 'Beleg hat' : 'Belege haben'} ein Datum außerhalb {year}.</strong>{' '}
                  Bitte prüfen — KI-Lesefehler oder alter Beleg? Tippen zum Korrigieren.
                </span>
              </div>
            );
          })()}
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Diese Sitzung — {stackList.length} {stackList.length === 1 ? 'Beleg' : 'Belege'}
            </div>
            {/* Feb 2026 — Stapel-Statistik */}
            {(() => {
              const total = stackList.reduce((s, b) => s + (b.amount || 0), 0);
              const categories = new Set(stackList.map(b => b.category)).size;
              return (
                <div className="text-[10px] text-emerald-400 font-semibold" data-testid="scan-stack-stats">
                  Σ {total.toFixed(2)} € · {categories} Kat.
                </div>
              );
            })()}
          </div>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {stackList.map((s) => {
              const offYear = s.date && !String(s.date).startsWith(String(new Date().getFullYear()));
              return (
              <div
                key={s.id}
                className="w-full flex items-center justify-between bg-slate-900 border border-slate-800 rounded-md px-2.5 py-1.5 text-xs"
                data-testid={`scan-stack-item-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => { if (s.expenseId) navigate(`/finances?openExpense=${s.expenseId}`); }}
                  className="flex-1 min-w-0 text-left hover:opacity-80 transition"
                  title={s.expenseId ? "Tap zum Korrigieren/Nachpflegen" : ""}
                  data-testid={`scan-stack-open-${s.id}`}
                >
                  <div className="text-white truncate font-medium">{s.vendor}</div>
                </button>
                {/* Sep 2026 — Kategorie-Chip → Bottom-Sheet mit Kategorie-Liste */}
                <button
                  type="button"
                  disabled={!s.expenseId || !!s.categorySaving}
                  onClick={() => setCategoryPickerFor(s.id)}
                  className="ml-1 shrink-0 max-w-[96px] truncate rounded border border-slate-700 bg-slate-800 px-1.5 py-1 text-[10px] font-medium text-slate-300 hover:bg-slate-700 transition disabled:opacity-60"
                  title="Kategorie antippen zum Ändern"
                  data-testid={`scan-stack-category-${s.id}`}
                >
                  {s.categorySaving ? '…' : catLabel(s.category)}
                </button>
                {/* Sep 2026 — Belegdatum direkt in der Liste ändern (nativer Date-Picker, kein Drawer) */}
                <label
                  className={`relative ml-2 inline-flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-medium cursor-pointer border transition ${
                    offYear
                      ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                      : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
                  } ${s.dateSaving ? 'opacity-60' : ''}`}
                  title={offYear ? 'Belegdatum liegt nicht im aktuellen Jahr — antippen zum Korrigieren' : 'Belegdatum antippen zum Ändern'}
                  data-testid={`scan-stack-date-${s.id}`}
                >
                  {offYear ? <AlertTriangle className="h-2.5 w-2.5" /> : <CalendarDays className="h-2.5 w-2.5" />}
                  <span data-testid={`scan-stack-date-label-${s.id}`}>{s.dateSaving ? '…' : s.date}</span>
                  <input
                    type="date"
                    value={s.date || ''}
                    disabled={!s.expenseId || !!s.dateSaving}
                    onChange={(e) => updateStackDate(s, e.target.value)}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                    data-testid={`scan-stack-date-input-${s.id}`}
                    aria-label="Belegdatum ändern"
                  />
                </label>
                {/* Sep 2026 — Betrag direkt in der Liste ändern (Tap → Inline-Eingabe) */}
                {editingAmountId === s.id ? (
                  <form
                    className="ml-2 shrink-0 flex items-center gap-1"
                    onSubmit={(e) => { e.preventDefault(); commitStackAmount(s); }}
                    data-testid={`scan-stack-amount-form-${s.id}`}
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      autoFocus
                      value={amountDraft}
                      onChange={(e) => setAmountDraft(e.target.value)}
                      onBlur={() => commitStackAmount(s)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { editingAmountRef.current = null; setEditingAmountId(null); } }}
                      className="w-20 rounded border border-emerald-500/60 bg-slate-950 px-1.5 py-1 text-right text-xs font-semibold text-emerald-300 outline-none focus:ring-1 focus:ring-emerald-400"
                      data-testid={`scan-stack-amount-input-${s.id}`}
                      aria-label="Betrag ändern"
                    />
                    <span className="text-[10px] text-slate-400">{s.currency}</span>
                  </form>
                ) : (
                  <button
                    type="button"
                    disabled={!s.expenseId || !!s.amountSaving}
                    onClick={() => { setAmountDraft(s.amount.toFixed(2).replace('.', ',')); editingAmountRef.current = s.id; setEditingAmountId(s.id); }}
                    className="ml-2 shrink-0 rounded px-1.5 py-1 text-emerald-400 font-semibold text-xs border border-transparent hover:border-emerald-500/40 hover:bg-emerald-500/10 transition disabled:opacity-60"
                    title="Betrag antippen zum Ändern"
                    data-testid={`scan-stack-amount-${s.id}`}
                  >
                    {s.amountSaving ? '…' : `${s.amount.toFixed(2)} ${s.currency}`}
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sep 2026 — Kategorie-Auswahl (Bottom-Sheet) */}
      {categoryPickerFor && (() => {
        const entry = stackList.find(x => x.id === categoryPickerFor);
        if (!entry) return null;
        return (
          <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/60" onClick={() => setCategoryPickerFor(null)}
            data-testid="scan-category-sheet">
            <div className="rounded-t-2xl bg-slate-900 border-t border-slate-700 px-4 pt-3 pb-6 max-h-[70vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}>
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-700" />
              <div className="mb-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">Kategorie wählen</div>
                  <div className="text-[11px] text-slate-400 truncate">{entry.vendor} · {entry.amount.toFixed(2)} {entry.currency}</div>
                </div>
                <button type="button" onClick={() => setCategoryPickerFor(null)} className="p-1.5 text-slate-400 hover:text-white"
                  data-testid="scan-category-sheet-close" aria-label="Schließen">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {EXPENSE_CATEGORIES.map(c => {
                  const active = c.key === entry.category;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => updateStackCategory(entry, c.key)}
                      className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-left text-xs font-medium transition ${
                        active
                          ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                          : 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                      }`}
                      data-testid={`scan-category-option-${c.key}`}
                    >
                      <span className="truncate">{c.label}</span>
                      {active && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" />}
                    </button>
                  );
                })}
              </div>
              {entry.category !== 'hospitality' && (
                <p className="mt-3 text-[10px] text-slate-500">Tipp: „Bewirtung" setzt die Absetzquote automatisch (DE 70 % / AT 50 %).</p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Pages-Stack */}
      <div className="flex-1 px-4 py-6 overflow-y-auto">
        {pages.length === 0 ? (
          <EmptyState onStart={openCamera} quickMode={quickMode} />
        ) : (
          <PagesPreview pages={pages} onRemove={removePage} onCrop={(id) => setCropDraft({ pageId: id })} quickMode={quickMode} analyzing={analyzing} />
        )}
      </div>

      {/* Bottom-Action-Bar */}
      {pages.length > 0 && (
        <div className="border-t border-slate-800 bg-slate-900 px-4 py-3 space-y-2 sticky bottom-0">
          {/* Jun 2026 — Fortschritt der sequenziellen Stapel-Verarbeitung */}
          {batchProgress && (
            <div className="space-y-1" data-testid="scan-batch-progress">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-300 font-medium" data-testid="scan-batch-progress-label">
                  Beleg {Math.min(batchProgress.done + 1, batchProgress.total)} von {batchProgress.total}
                </span>
                <span className="text-slate-500">
                  {batchProgress.done} fertig
                  {batchProgress.failed > 0 && <span className="text-rose-400"> · {batchProgress.failed} Fehler</span>}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-[width] duration-500 ease-out"
                  style={{ width: `${Math.round((batchProgress.done / batchProgress.total) * 100)}%` }}
                  data-testid="scan-batch-progress-bar"
                />
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={openCamera} variant="outline" disabled={analyzing}
              className="flex-1 bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
              data-testid="scan-add-page-btn">
              <Plus className="h-4 w-4 mr-1.5" />
              {quickMode ? 'Weitere Belege' : 'Weitere Seite'}
            </Button>
            <Button onClick={quickMode ? processQueue : analyzePages} disabled={analyzing}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="scan-analyze-btn">
              {analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />}
              {analyzing
                ? (quickMode && batchProgress ? `Verarbeite ${Math.min(batchProgress.done + 1, batchProgress.total)}/${batchProgress.total}…` : 'Analyse läuft…')
                : (quickMode ? `${pages.length} ${pages.length === 1 ? 'Beleg' : 'Belege'} analysieren` : 'Fertig & analysieren')}
            </Button>
          </div>
          <button onClick={resetAll} disabled={analyzing} className="w-full text-xs text-slate-500 hover:text-slate-300 py-1 disabled:opacity-40"
            data-testid="scan-reset-btn">
            Alles verwerfen
          </button>
        </div>
      )}
    </div>
  );
}

// ───────────────── Sub-Components ─────────────────

function EmptyState({ onStart, quickMode }) {
  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-emerald-500/10 mb-6">
        <Camera className="h-12 w-12 text-emerald-400" />
      </div>
      <h2 className="text-xl font-bold mb-2">
        {quickMode ? 'Stapel starten' : 'Beleg fotografieren'}
      </h2>
      <p className="text-sm text-slate-400 mb-8 max-w-xs mx-auto leading-relaxed">
        {quickMode
          ? 'Fotografiere alle Belege schnell hintereinander — danach genügt ein Klick auf „Analysieren" und die KI erkennt und speichert alles automatisch.'
          : 'Quittung, Rechnung oder mehrseitiger Lieferantenbeleg. KI erkennt Vendor, Datum & Betrag automatisch.'}
      </p>
      <Button onClick={onStart} size="lg"
        className="bg-emerald-500 hover:bg-emerald-600 text-white text-base px-8 py-6 rounded-full"
        data-testid="scan-start-btn">
        <Camera className="h-5 w-5 mr-2" />
        {quickMode ? 'Ersten Beleg scannen' : 'Scannen starten'}
      </Button>
    </div>
  );
}

function PagesPreview({ pages, onRemove, onCrop, quickMode = false, analyzing = false }) {
  return (
    <div>
      <div className="text-xs text-slate-400 mb-3 uppercase tracking-wider">
        {pages.length} {quickMode
          ? (pages.length === 1 ? 'Beleg' : 'Belege')
          : (pages.length === 1 ? 'Seite' : 'Seiten')} erfasst
      </div>
      <div className="grid grid-cols-3 gap-2">
        {pages.map((p, i) => (
          <div key={p.id || i}
            className={`relative aspect-[3/4] bg-slate-800 rounded-lg overflow-hidden ${p.status === 'error' ? 'ring-2 ring-rose-500' : ''}`}
            data-testid={`scan-page-${i}`}>
            <img src={p.url} alt={`Seite ${i + 1}`} className="w-full h-full object-cover" />
            {p.cropping && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-[10px] gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Zuschnitt…</span>
              </div>
            )}
            {!p.cropping && p.needsCrop && p.status !== 'processing' && (
              <div className="absolute bottom-0 inset-x-0 bg-amber-500/90 text-slate-950 text-[9px] px-1 py-0.5 text-center font-semibold"
                data-testid={`scan-page-needscrop-${i}`}>
                Zuschnitt prüfen
              </div>
            )}
            {p.status === 'queued' && (
              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-slate-300 text-[10px] gap-1"
                data-testid={`scan-page-queued-${i}`}>
                <Clock className="h-4 w-4" />
                <span>Wartet…</span>
              </div>
            )}
            {p.status === 'processing' && (
              <div className="absolute inset-0 bg-emerald-950/70 ring-2 ring-emerald-400 ring-inset flex flex-col items-center justify-center text-white text-[10px] gap-1"
                data-testid={`scan-page-processing-${i}`}>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{STEP_LABELS[p.step] || 'Startet…'}</span>
              </div>
            )}
            {p.status === 'error' && (
              <div className="absolute bottom-0 inset-x-0 bg-rose-600/90 text-white text-[9px] px-1 py-0.5 text-center leading-tight"
                data-testid={`scan-page-error-${i}`} title={p.errorMsg || ''}>
                <div className="font-semibold">Fehler</div>
                <div className="truncate opacity-90">{p.errorMsg || 'erneut analysieren'}</div>
              </div>
            )}
            <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
              {i + 1}
            </div>
            {!analyzing && (
              <button onClick={() => onRemove(p.id)}
                className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full hover:bg-rose-500"
                data-testid={`scan-remove-page-${i}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            )}
            {/* Sep 2026 — Zuschnitt manuell anpassen (Editor mit verschiebbaren Ecken) */}
            {!analyzing && !p.cropping && onCrop && (
              <button onClick={() => onCrop(p.id)}
                className="absolute top-7 right-1 bg-black/60 text-white p-1 rounded-full hover:bg-emerald-500"
                title="Zuschnitt anpassen" aria-label="Zuschnitt anpassen"
                data-testid={`scan-crop-page-${i}`}>
                <Crop className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AiResultView({ aiResult, saving, onSave, onDiscard, onCorrect }) {
  const ed = aiResult.extracted_data || {};
  const conf = ed.confidence ?? 0;
  const confColor = conf >= 85 ? 'text-emerald-400' : conf >= 60 ? 'text-amber-400' : 'text-rose-400';
  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="scan-result">
      <header className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <button onClick={onCorrect} className="p-2 -ml-2 text-slate-400" data-testid="scan-result-back">
          <X className="h-5 w-5" />
        </button>
        <div className="text-sm font-semibold">KI-Erkennung</div>
        <div className={`text-xs ${confColor}`}>{conf}% sicher</div>
      </header>

      <div className="flex-1 px-4 py-6 space-y-4">
        {aiResult.thumbnail && (
          <div className="bg-slate-900 rounded-lg overflow-hidden mx-auto max-w-[180px] aspect-[3/4]">
            <img src={aiResult.thumbnail} alt="Beleg-Vorschau" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="bg-slate-900 rounded-xl p-4 space-y-3">
          <FieldRow label="Vendor" value={ed.vendor || '—'} />
          <FieldRow label="Betrag" value={ed.amount ? `${Number(ed.amount).toFixed(2)} ${ed.currency || 'EUR'}` : '—'} />
          <FieldRow label="Datum" value={ed.date || '—'} />
          <FieldRow label="Kategorie" value={ed.category_suggestion || 'other'} />
          {(() => {
            const bd = Array.isArray(ed.tax_breakdown)
              ? ed.tax_breakdown.filter((tb) => tb && (Number(tb.net) > 0 || Number(tb.vat) > 0))
              : [];
            if (bd.length >= 2) {
              return (
                <div className="pt-1" data-testid="mobilescan-tax-breakdown">
                  <p className="text-xs text-slate-400 mb-1">MwSt-Sätze (erkannt)</p>
                  {bd.map((tb, i) => (
                    <div key={i} className="flex items-center justify-between text-sm text-slate-200 py-0.5">
                      <span>{Number(tb.rate)}%</span>
                      <span className="font-mono text-xs text-slate-400">
                        netto {Number(tb.net).toFixed(2)} · USt {Number(tb.vat).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }
            return ed.vat_rate ? <FieldRow label="USt" value={`${ed.vat_rate}%`} /> : null;
          })()}
        </div>

        {aiResult.pages_processed > 1 && (
          <div className="text-xs text-slate-400 bg-slate-900 rounded-lg p-3 flex items-start gap-2">
            <FileText className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>{aiResult.pages_processed} Seiten verarbeitet — alle Daten konsolidiert.</span>
          </div>
        )}

        {conf < 60 && (
          <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Niedrige Erkennungssicherheit — bitte vor dem Speichern korrigieren.</span>
          </div>
        )}
      </div>

      <div className="border-t border-slate-800 bg-slate-900 px-4 py-3 space-y-2 sticky bottom-0">
        <Button onClick={onSave} disabled={saving}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="scan-result-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />}
          Speichern
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={onCorrect} variant="outline"
            className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
            data-testid="scan-result-correct">
            Erneut scannen
          </Button>
          <Button onClick={onDiscard} variant="outline"
            className="bg-slate-800 border-slate-700 text-rose-400 hover:bg-slate-700"
            data-testid="scan-result-discard">
            Verwerfen
          </Button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="text-white font-medium">{value}</span>
    </div>
  );
}

function DesktopRedirect() {
  const url = `${window.location.origin}/m/scan`;
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="scan-desktop-redirect">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <Smartphone className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Mobile-Scan</h1>
        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
          Diese Seite ist für Smartphones optimiert. Scanne den QR-Code oder öffne den Link auf deinem Handy.
        </p>
        <div className="bg-white rounded-xl p-4 inline-block mb-4 border border-slate-200">
          <QRCodeSVG value={url} size={180} />
        </div>
        <p className="text-xs text-slate-500 break-all font-mono bg-slate-50 px-3 py-2 rounded">{url}</p>
        <p className="text-[11px] text-slate-400 mt-4">
          Tipp: Aktiviere im Login „Angemeldet bleiben (90 Tage)" für komfortables tägliches Scannen.
        </p>
      </div>
    </div>
  );
}
